
import { of as observableOf, Observable, ReplaySubject, Subject, EMPTY } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { SearchResult, FileSearchResult, UnifiedSearchResult } from './searchResult';
import { egnyteSnippetHtml, graphSummaryHtml } from './search-snippet';
import { UserService } from './user.service';
import { AuthService } from '../auth.service';



@Injectable()
export class SearchService {

  private url = environment.cubCloudUrl + 'search';
  private fileUrl = environment.cubCloudUrl + 'file/search';
  private isSearching = new ReplaySubject<boolean>(1);
  isSearching$ = this.isSearching.asObservable();
  private showSearchPopup = new ReplaySubject<boolean>(1);
  showSearchPopup$ = this.showSearchPopup.asObservable();
  private searchResults = new ReplaySubject<SearchResult[]>(1);
  searchResults$ = this.searchResults.asObservable();
  private fileSearchResults = new ReplaySubject<FileSearchResult[]>(1);
  fileSearchResults$ = this.fileSearchResults.asObservable();
  private term = new ReplaySubject<string>(1);
  term$ = this.term.asObservable();
  // Surfaces the status of the last SharePoint (Microsoft Graph) search: '' = ok, otherwise an error message.
  private sharePointError = new ReplaySubject<string>(1);
  sharePointError$ = this.sharePointError.asObservable();
  // Keyboard navigation commands raised from the toolbar search box so the quick-search popup can
  // move its highlighted row while the input keeps focus.
  private popupKeyNav = new Subject<'up' | 'down' | 'enter'>();
  popupKeyNav$ = this.popupKeyNav.asObservable();
  lastTerm: string;
  public lastSystem: string;

  constructor(
    private userService: UserService,
    private http: HttpClient,
    private authService: AuthService
  ) {
    this.showSearchPopup.next(false);
    this.isSearching.next(false);
  }

  setSystem(system: string) {
    this.lastSystem = system;
  }

  // Pushes a term into the shared stream so the unified search page (and top-menu box) react to it.
  // Used to seed the search from the URL (?q=...) on a page refresh / deep link.
  setTerm(term: string) {
    this.lastTerm = term;
    this.term.next(term);
  }

  searchBySystem(system: string, folder: string) {
    let terms: Observable<string>;
    this.lastSystem = system;
    terms = observableOf(this.lastTerm);
    return terms.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap(term => this.searchEntries(this.lastTerm, system, folder)));
  }

  search(terms: Observable<string>, system: string) {
    this.lastSystem = system;
    return terms.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap(term => this.searchEntries(term, system, '')));
  }

  searchByTerm(terms: Observable<string>) {
    return terms.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap(term => this.searchEntries(term, this.lastSystem, '')));
  }

  searchEntries(term: string, system: string, folder: string): Observable<any> {
    this.showSearchPopup.next(true);
    this.isSearching.next(true);
    this.lastTerm = term;
    this.term.next(term);
    let url = `${this.url}/${term}/${system}`;
    let fileUrl = `${this.fileUrl}/${term}`;

    if (term.length === 0) {
      this.clearResults();
      this.closeSearch();
    }
    console.log('searching', term, system, folder);
    if (system === 'File' && term.length > 2) {
      return this.http
        .get<any>(fileUrl, { headers: this.userService.getUrlUserHeaders('application/json'), withCredentials: false });
    } else {
      if (term.length > 0) {
        return this.http
          .post<any>(
            url,
            JSON.stringify({ folder: '/Shared' + (folder.length === 0 ? '' : '/' + folder) }),
            { headers: this.userService.getUrlUserHeaders('application/json'), withCredentials: false });
      } else {
        return EMPTY;
      }
    }
  }

  mapResult(result) {
    if (this.lastSystem === 'File') {
      this.fileSearchResults.next(result);
    } else {
      this.searchResults.next(result);
    }
    this.isSearching.next(false);
    return result;
  }

  // Clears the quick-search popup results so nothing stale lingers when the box is emptied.
  clearResults() {
    this.lastTerm = '';
    this.searchResults.next([]);
    this.fileSearchResults.next([]);
    this.isSearching.next(false);
  }

  // ---- Unified enterprise search (CUB + Egnyte + SharePoint), all run as the current user ----

  // Runs all three sources in parallel and returns a single merged, source-tagged list.
  searchAll(term: string): Observable<UnifiedSearchResult[]> {
    if (!term || term.length < 2) {
      return observableOf([]);
    }
    this.lastTerm = term;
    return forkJoin({
      cub: this.searchCub(term),
      egnyte: this.searchEgnyte(term),
      sharepoint: this.searchSharePoint(term)
    }).pipe(
      map(r => [...r.cub, ...r.sharepoint, ...r.egnyte])
    );
  }

  // Fetches only the file-backed sources (Egnyte + SharePoint) for the quick-search popup's
  // category summary. CUB is intentionally excluded — the popup already lists the top CUB hits
  // via the legacy searchResults$ stream, and these two are all that feed the Property Files /
  // Lease Files / Egnyte / SharePoint counts.
  searchFileSources(term: string): Observable<UnifiedSearchResult[]> {
    if (!term || term.length < 3) {
      return observableOf([]);
    }
    return forkJoin({
      egnyte: this.searchEgnyte(term),
      sharepoint: this.searchSharePoint(term)
    }).pipe(
      map(r => [...r.egnyte, ...r.sharepoint])
    );
  }

  private searchCub(term: string): Observable<UnifiedSearchResult[]> {    // systems="CUB" makes the backend return both the in-memory ID matches and CUB table matches.
    const url = `${this.url}/${term}/CUB`;
    return this.http.post<SearchResult[]>(
      url,
      JSON.stringify({ folder: '/Shared' }),
      { headers: this.userService.getUrlUserHeaders('application/json'), withCredentials: false }
    ).pipe(
      map(results => (results || []).map(r => new UnifiedSearchResult({
        source: 'CUB',
        title: r.description,
        subtitle: r.location || r.type,
        snippet: '',
        url: r.url,
        externalLink: r.externalLink,
        typeHint: r.type,
        group: 'CUB'
      }))),
      catchError(() => observableOf([]))
    );
  }

  private searchEgnyte(term: string): Observable<UnifiedSearchResult[]> {
    if (term.length < 3) {
      return observableOf([]);
    }
    // Backend adds X-Egnyte-Act-As using the cub-user header, so Egnyte enforces this user's permissions.
    const fileUrl = `${this.fileUrl}/${term}`;
    return this.http.get<FileSearchResult[]>(
      fileUrl,
      { headers: this.userService.getUrlUserHeaders('application/json'), withCredentials: false }
    ).pipe(
      map(results => (results || []).map(r => {
        // Egnyte "Property Files" (path ".../Shared/Property Files/<property>/...") get their own
        // category, sub-grouped by property folder — mirroring the SharePoint Lease Files grouping.
        const prop = this.egnytePropertyParts(r.path);
        return new UnifiedSearchResult({
          source: 'Egnyte',
          title: r.name,
          subtitle: r.path,
          snippet: egnyteSnippetHtml(r.snippet),
          url: r.path,
          externalLink: true,
          typeHint: r.isFolder ? 'folder' : 'file',
          group: prop.isProperty ? 'Property Files' : 'Egnyte',
          leaseName: prop.isProperty ? (prop.property || 'Other') : undefined,
          folderName: prop.isProperty ? prop.folder : undefined
        });
      })),
      catchError(() => observableOf([]))
    );
  }

  // Uses the Microsoft Graph Search API with the user's delegated token, so results are
  // automatically limited to what the signed-in user is allowed to see in SharePoint.
  private searchSharePoint(term: string): Observable<UnifiedSearchResult[]> {
    if (term.length < 3) {
      return observableOf([]);
    }
    const body = {
      requests: [{
        // driveItem covers SharePoint document-library files + OneDrive; listItem adds SharePoint
        // pages/list items. Both are combinable in one request. listItem requires the delegated
        // Sites.Read.All permission (now consented) in addition to Files.Read.All.
        entityTypes: ['driveItem', 'listItem'],
        query: { queryString: term },
        from: 0,
        size: 100
      }]
    };
    return this.authService.getGraphToken().pipe(
      switchMap(graphToken => {
        const headers = new HttpHeaders()
          .append('Content-Type', 'application/json')
          .append('Authorization', 'Bearer ' + graphToken);
        return this.http.post<any>('https://graph.microsoft.com/v1.0/search/query', body, { headers });
      }),
      map(resp => {
        const out: UnifiedSearchResult[] = [];
        const containers = resp?.value || [];
        for (const container of containers) {
          const hitsContainers = container?.hitsContainers || [];
          for (const hc of hitsContainers) {
            for (const hit of (hc.hits || [])) {
              const resource = hit.resource || {};
              const odataType = (resource['@odata.type'] || '').toLowerCase();
              const name = resource.name || resource.title || (resource.fields && resource.fields.title) || 'Untitled';
              const webUrl = resource.webUrl || (resource.fields && resource.fields.linkToItem) || '';
              const group = this.sharePointLocation(resource, webUrl);
              // A folder in a document library is a driveItem with a "folder" facet. Search doesn't
              // always return the facet, so also treat an extension-less name as a folder.
              const isFolder = !!resource.folder || (group === 'Lease Files' && !this.hasFileExtension(name));
              const isListItem = !isFolder &&
                (odataType.includes('listitem') || odataType.includes('.site') || odataType.includes('.list'));
              const lease = group === 'Lease Files' ? this.leasePathParts(webUrl) : null;
              out.push(new UnifiedSearchResult({
                source: 'SharePoint',
                title: name,
                subtitle: this.sharePointSubtitle(resource),
                snippet: graphSummaryHtml(hit.summary),
                url: webUrl,
                externalLink: true,
                typeHint: isFolder ? 'folder' : (isListItem ? 'listItem' : 'file'),
                group,
                leaseName: lease ? lease.lease : undefined,
                folderName: lease ? lease.folder : undefined
              }));
            }
          }
        }
        this.sharePointError.next('');
        return out;
      }),
      catchError(err => {
        const status = err?.status;
        const graphMsg = err?.error?.error?.message || err?.message || 'Unknown error';
        console.error('SharePoint (Graph) search failed', status, graphMsg, err);
        this.sharePointError.next(`SharePoint search failed (${status || 'network'}): ${graphMsg}`);
        return observableOf([]);
      })
    );
  }

  private sharePointSubtitle(resource: any): string {
    return resource?.parentReference?.path || resource?.webUrl || '';
  }

  private hasFileExtension(name: string): boolean {
    return /\.[A-Za-z0-9]{1,6}$/.test(name || '');
  }

  // Lease Files live at ".../Lease Documents/<lease>/<folder>/<file>". The lease name is the segment
  // right after "Lease Documents". A folder name only exists when there is a segment BETWEEN the
  // lease and the item itself (i.e. 3+ segments); a file sitting directly in the lease folder has no
  // folder and must not treat its own filename as a folder.
  private leasePathParts(webUrl: string): { lease: string; folder: string } {
    try {
      const path = decodeURIComponent(webUrl || '');
      const marker = '/lease documents/';
      const idx = path.toLowerCase().indexOf(marker);
      if (idx === -1) {
        return { lease: '', folder: '' };
      }
      const segs = path.substring(idx + marker.length).split('/').filter(s => s.length);
      const lease = segs[0] || '';
      const folder = segs.length >= 3 ? segs[1] : '';
      return { lease, folder };
    } catch {
      return { lease: '', folder: '' };
    }
  }

  // Egnyte "Property Files" live at ".../Shared/Property Files/<property>/<folder>/<file>". The
  // property folder is the segment right after "Property Files"; like leases, a folder name only
  // exists when there is a segment BETWEEN the property and the item itself (3+ segments).
  private egnytePropertyParts(path: string): { isProperty: boolean; property: string; folder: string } {
    try {
      const p = decodeURIComponent(path || '');
      const marker = '/shared/property files/';
      const idx = p.toLowerCase().indexOf(marker);
      if (idx === -1) {
        return { isProperty: false, property: '', folder: '' };
      }
      const segs = p.substring(idx + marker.length).split('/').filter(s => s.length);
      const property = segs[0] || '';
      const folder = segs.length >= 3 ? segs[1] : '';
      return { isProperty: !!property, property, folder };
    } catch {
      return { isProperty: false, property: '', folder: '' };
    }
  }

  // Derives a location label for a SharePoint/OneDrive hit so each location can have its own pill.
  // OneDrive lives under "<tenant>-my.sharepoint.com/personal/...", site files under ".../sites/<Name>/...".
  private sharePointLocation(resource: any, webUrl: string): string {
    const url = (webUrl || resource?.webUrl || '').toLowerCase();
    const driveType = (resource?.parentReference?.driveType || '').toLowerCase();
    if (driveType === 'personal' || url.includes('-my.sharepoint.com') || url.includes('/personal/')) {
      return 'OneDrive';
    }
    const m = (webUrl || '').match(/\/(?:sites|teams)\/([^\/]+)/i);
    if (m && m[1]) {
      const site = decodeURIComponent(m[1]);
      // Collapse the PropertyFiles001/002/... document sites into one friendly "Lease Files" pill.
      if (/^propertyfiles\d+$/i.test(site)) {
        return 'Lease Files';
      }
      return site;
    }
    return 'SharePoint';
  }

  closeSearch() {
    this.showSearchPopup.next(false);
  }

  openSearch() {
    this.showSearchPopup.next(true);
  }

  // Raised by the toolbar search box on ArrowUp/ArrowDown/Enter so the popup can move/activate its
  // highlighted row without the input losing focus.
  popupKeyCommand(command: 'up' | 'down' | 'enter') {
    this.popupKeyNav.next(command);
  }

}
